<template>
  <div class="form-inline mb-3">
    <label for="selected-group" class="control-label mr-2">{{ $t('selectGroup:selectYourGroup', {group: $t('selectGroup:'+group)}) | capitalize }}</label>
    <select id="selected-group" v-model="selectedGroupId" class="form-control">
      <option v-if="all" value="-1">{{ $t('selectGroup:allGroups', {group: $t('selectGroup:'+group, {context: 'plural'})}) | capitalize }}</option>

      <template v-if="isRole.superAdmin">
        <optgroup v-for="(groups, customer) in groupedList" :key="customer" :label="customer">
          <option v-for="g in groups" :key="g.id" :value="g.id">{{ g.name }}</option>
        </optgroup>
      </template>

      <template v-else>
        <option v-for="g in list" :key="g.id" :value="g.id">{{ g.name }}</option>
      </template>
    </select>
  </div>
</template>

<script>
import { groupBy, orderBy, findIndex } from 'lodash-es'
import { mapGetters } from 'vuex'

export default {
  props: {
    // What to select [fleet|cluster]
    group: {
      type: String,
      required: true
    },
    // Display "all [group]" option
    all: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      selectedGroupId: ''
    }
  },

  computed: {
    // mix the getters into computed with object spread operator
    ...mapGetters([
      'authenticatedUser',
      'allFleets', 'selectedFleetId',
      'allClusters', 'selectedClusterId'
    ]),

    // List of fleets or clusters
    list () {
      if (this.group === 'fleet') return this.allFleets
      else if (this.group === 'cluster') return this.allClusters
      else return []
    },

    // List of fleets or clusters grouped by customer for superAdmin
    groupedList () {
      // Order by customer name
      // Group by customer name to add customer in lists
      if (this.group === 'fleet') return groupBy(orderBy(this.allFleets, ['customer.name']), 'customer.name')
      else if (this.group === 'cluster') return groupBy(orderBy(this.allClusters, ['customer.name']), 'customer.name')
      else return {}
    }
  },

  watch: {
    selectedGroupId (newGroupId) {
      // Save new selected group id
      if (this.group === 'fleet') {
        this.$store.dispatch('saveSelectedFleetId', newGroupId)
      } else if (this.group === 'cluster') {
        this.$store.dispatch('saveSelectedClusterId', newGroupId)
      }
    }
  },

  mounted () {
    if (this.group === 'fleet') {
      this.$store.dispatch('getAllFleets').then(this.selectFleet)
    } else if (this.group === 'cluster') {
      this.$store.dispatch('getAllClusters').then(this.selectCluster)
    }
  },

  methods: {
    // Check if selectedFleet exists in list and decide which fleet to select
    selectFleet () {
      const storeSelectedFleetId = Number(this.selectedFleetId)
      const savedSelectedFleetId = Number(localStorage.getItem(this.authenticatedUser.login + '-selectedFleetId'))

      if (savedSelectedFleetId) {
        // Check selected fleet in local storage
        if (findIndex(this.allFleets, fleet => fleet.id === savedSelectedFleetId) !== -1) this.selectedGroupId = savedSelectedFleetId
        else this.selectedGroupId = -1
      } else if (storeSelectedFleetId) {
        // Check selected fleet in store (previously selected)
        if (findIndex(this.allFleets, fleet => fleet.id === storeSelectedFleetId) !== -1) this.selectedGroupId = storeSelectedFleetId
        else this.selectedGroupId = -1
      } else {
        // No selected fleet in store nor local storage, select the first fleet of the list
        this.selectedGroupId = this.allFleets[0].id
      }

      // All fleet not displayed, select first of the list if "all" was selected before
      if (!this.all && this.selectedGroupId === -1) {
        this.selectedGroupId = this.allFleets[0].id
      }
    },

    // Check if selectedCluster exists in list and decide which cluster to select
    selectCluster () {
      const storeSelectedClusterId = Number(this.selectedClusterId)
      const savedSelectedClusterId = Number(localStorage.getItem(this.authenticatedUser.login + '-selectedClusterId'))

      if (savedSelectedClusterId) {
        // Check selected cluster in local storage
        if (findIndex(this.allClusters, cluster => cluster.id === savedSelectedClusterId) !== -1) this.selectedGroupId = savedSelectedClusterId
        else this.selectedGroupId = -1
      } else if (storeSelectedClusterId) {
        // Check selected cluster in store (previously selected)
        if (findIndex(this.allClusters, cluster => cluster.id === storeSelectedClusterId) !== -1) this.selectedGroupId = storeSelectedClusterId
        else this.selectedGroupId = -1
      } else {
        // No selected cluster in store nor local storage, select the first cluster of the list
        this.selectedGroupId = this.allClusters[0].id
      }

      // All clusters not displayed, select first of the list if "all" was selected before
      if (!this.all && this.selectedGroupId === -1) {
        this.selectedGroupId = this.allClusters[0].id
      }
    }
  }
}
</script>

<style scoped lang="scss">
  @import "~@/scss/variables";

  #selected-group {
    min-width: 200px;
  }
</style>
